/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPIHousien } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export interface ClientInterestRecord {
  interest_id: string;
  client_id: string;
  property_id: string;
  saved_date: string;
  notes: string | null;
  intent: "buy" | "lease" | "both";
  agency_id: string;
}

/**
 * Lists a client's saved property interests via
 * `GET /agency/{agencyId}/clients/{client_id}/interests`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client whose interests are listed
 * @returns A cancelable promise resolving to the client's interest records
 */
export function getClientInterests(agencyId: string, clientId: string): CancelablePromise<ClientInterestRecord[]> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agency/{agencyId}/clients/{client_id}/interests",
    path: { agencyId, client_id: clientId },
  });
}

export interface ClientReferral {
  referral_id: string;
  referred_name: string;
  referred_phone: string;
  status: "pending" | "contacted" | "converted";
  created_at: string;
  /**
   * Client created/linked when the indication was converted.
   */
  /** Set by the server when the referral converts. */
  converted_client_id?: string | null;
}

export interface ClientInteraction {
  interaction_id: string;
  channel: string;
  kind?: "interaction";
  comment: string | null;
  created_by?: string | null;
  occurred_at: string;
}

export interface ClientSummary {
  total_visits: number;
  properties_seen: number;
  active_negotiations: number;
  total_contracts: number;
  last_visit_date: string | null;
  last_contract_date: string | null;
}

export interface ClientTimelineEvent {
  event_type: "visit" | "negotiation" | "contract" | "interest";
  date: string;
  entity_id?: string;
  status?: string;
  property_ids?: string[];
  property_id?: string;
}


/**
 * Lists referrals made by a client via
 * `GET /agency/{agencyId}/clients/{client_id}/referrals`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The client identifier
 * @returns A cancelable promise resolving to the client's referrals
 */
export function getClientReferrals(agencyId: string, clientId: string): CancelablePromise<ClientReferral[]> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agency/{agencyId}/clients/{client_id}/referrals",
    path: { agencyId, client_id: clientId },
  });
}

/**
 * Creates a new referral for a client via
 * `POST /agency/{agencyId}/clients/{client_id}/referrals`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The referring client
 * @param body - The referred person's name and phone
 * @returns A cancelable promise resolving to the created referral (status `pending`)
 */
/**
 * Creates a new referral for a client via
 * `POST /agency/{agencyId}/clients/{client_id}/referrals`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The referring client identifier
 * @param body - The referred person's name and phone
 * @returns A cancelable promise resolving to the created referral
 */
export function createClientReferral(
  agencyId: string, clientId: string, body: { referred_name: string; referred_phone: string },
): CancelablePromise<ClientReferral> {
  return __request(OpenAPIHousien, {
    method: "POST",
    url: "/agency/{agencyId}/clients/{client_id}/referrals",
    path: { agencyId, client_id: clientId },
    body,
    mediaType: "application/json",
  });
}

/**
 * Updates a referral's status (optionally linking a converted client) via
 * `PATCH /agency/{agencyId}/clients/{client_id}/referrals/{referral_id}`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The referring client identifier
 * @param referralId - The referral identifier
 * @param status - The new referral status
 * @returns A cancelable promise resolving to the updated referral
 */
export function updateReferralStatus(
  agencyId: string,
  clientId: string,
  referralId: string,
  status: string,
): CancelablePromise<ClientReferral> {
  return __request(OpenAPIHousien, {
    method: "PATCH",
    url: "/agency/{agencyId}/clients/{client_id}/referrals/{referral_id}",
    path: { agencyId, client_id: clientId, referral_id: referralId },
    body: { status },
    mediaType: "application/json",
  });
}

/**
 * Deletes a referral that has not been converted yet via
 * `DELETE /agency/{agencyId}/clients/{client_id}/referrals/{referral_id}`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The referring client
 * @param referralId - The referral to delete
 */
export function deleteClientReferral(
  agencyId: string,
  clientId: string,
  referralId: string,
): CancelablePromise<void> {
  return __request(OpenAPIHousien, {
    method: "DELETE",
    url: "/agency/{agencyId}/clients/{client_id}/referrals/{referral_id}",
    path: { agencyId, client_id: clientId, referral_id: referralId },
  });
}

/**
 * Lists a client's interactions via
 * `GET /agency/{agencyId}/clients/{client_id}/interactions`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client whose interactions are listed
 * @returns A cancelable promise resolving to the interaction records
 */
export function getClientInteractions(
  agencyId: string,
  clientId: string,
): CancelablePromise<ClientInteraction[]> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agency/{agencyId}/clients/{client_id}/interactions",
    path: { agencyId, client_id: clientId },
  });
}

/**
 * Records a new client interaction via
 * `POST /agency/{agencyId}/clients/{client_id}/interactions`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client the interaction belongs to
 * @param body - The interaction fields (channel, comment)
 * @returns A cancelable promise resolving to the persisted interaction
 */
/**
 * Records a new client interaction via
 * `POST /agency/{agencyId}/clients/{client_id}/interactions`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The client identifier
 * @param body - The partial interaction fields to persist
 * @returns A cancelable promise resolving to the created interaction
 */
export function createClientInteraction(
  agencyId: string, clientId: string, body: Partial<ClientInteraction>,
): CancelablePromise<ClientInteraction> {
  return __request(OpenAPIHousien, {
    method: "POST",
    url: "/agency/{agencyId}/clients/{client_id}/interactions",
    path: { agencyId, client_id: clientId },
    body,
    mediaType: "application/json",
  });
}

/**
 * Soft-deletes a broker-registered interaction record via
 * `DELETE /agency/{agencyId}/clients/{client_id}/interactions/{interaction_id}`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client the interaction belongs to
 * @param interactionId - The interaction to soft-delete
 * @returns A cancelable promise resolving to the deletion message payload
 */
export function deleteClientInteraction(
  agencyId: string,
  clientId: string,
  interactionId: string,
): CancelablePromise<{ message?: string }> {
  return __request(OpenAPIHousien, {
    method: "DELETE",
    url: "/agency/{agencyId}/clients/{client_id}/interactions/{interaction_id}",
    path: {
      agencyId,
      client_id: clientId,
      interaction_id: interactionId,
    },
  });
}

/**
 * Retrieves aggregated activity counters for a client via
 * `GET /agency/{agencyId}/clients/{client_id}/summary`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client whose summary is requested
 * @returns A cancelable promise resolving to visit/property/negotiation/contract totals
 */
export function getClientSummary(agencyId: string, clientId: string): CancelablePromise<ClientSummary> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agency/{agencyId}/clients/{client_id}/summary",
    path: { agencyId, client_id: clientId },
  });
}

/**
 * Retrieves a chronological timeline of client events via
 * `GET /agency/{agencyId}/clients/{client_id}/timeline`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client whose timeline is requested
 * @returns A cancelable promise resolving to visit/negotiation/contract/interest events
 */
export function getClientTimeline(agencyId: string, clientId: string): CancelablePromise<ClientTimelineEvent[]> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agency/{agencyId}/clients/{client_id}/timeline",
    path: { agencyId, client_id: clientId },
  });
}

export interface HistoryEvent {
  event_type: "visit" | "negotiation" | "interest" | "interaction";
  date: string;
  status?: string | null;
  entity_id: string;
  channel?: string | null;
  detail?: string | null;
  created_by?: string | null;
}

export interface PaginatedHistory {
  data: HistoryEvent[];
  first: number;
  prev: number | null;
  next: number | null;
  last: number;
  pages: number;
  items: number;
}

/**
 * Retrieves a paginated client history feed via
 * `GET /agency/{agencyId}/clients/{client_id}/history`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client whose history is requested
 * @param page - One-based page index (defaults to 1)
 * @param pageSize - Number of events per page (defaults to 20)
 * @param type - Optional event-type filter
 * @returns A cancelable promise resolving to the paginated history events
 */
/**
 * Retrieves a paginated, optionally type-filtered activity history for a client via
 * `GET /agency/{agencyId}/clients/{client_id}/history`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The client identifier
 * @param page = 1 - One-based page index
 * @param pageSize = 20 - Number of events per page
 * @param type - Optional event-type filter
 * @returns A cancelable promise resolving to the paginated history
 */
export function getClientHistory(
  agencyId: string,
  clientId: string,
  page: number = 1,
  pageSize: number = 20,
  type?: string,
): CancelablePromise<PaginatedHistory> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agency/{agencyId}/clients/{client_id}/history",
    path: { agencyId, client_id: clientId },
    query: { page, page_size: pageSize, ...(type ? { type } : {}) },
  });
}

/**
 * Structured free-text note on a client (Overview).
 */
export interface ClientNote {
  note_id: string;
  content: string;
  created_by: string;
  created_at: string;
  updated_at?: string | null;
}

/**
 * Lists free-text overview notes for a client via
 * `GET /agency/{agencyId}/clients/{client_id}/notes`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client whose notes are listed
 * @returns A cancelable promise resolving to the client's notes
 */
/**
 * Lists all free-text notes on a client via
 * `GET /agency/{agencyId}/clients/{client_id}/notes`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The client identifier
 * @returns A cancelable promise resolving to the client's notes
 */
export function getClientNotes(
  agencyId: string,
  clientId: string,
): CancelablePromise<ClientNote[]> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agency/{agencyId}/clients/{client_id}/notes",
    path: { agencyId, client_id: clientId },
  });
}

/**
 * Creates an overview note on a client via
 * `POST /agency/{agencyId}/clients/{client_id}/notes`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client the note belongs to
 * @param content - The note body text
 * @returns A cancelable promise resolving to the persisted note
 */
/**
 * Creates a free-text note on a client via
 * `POST /agency/{agencyId}/clients/{client_id}/notes`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The client identifier
 * @param content - The note body text
 * @returns A cancelable promise resolving to the created note
 */
export function createClientNote(
  agencyId: string,
  clientId: string,
  content: string,
): CancelablePromise<ClientNote> {
  return __request(OpenAPIHousien, {
    method: "POST",
    url: "/agency/{agencyId}/clients/{client_id}/notes",
    path: { agencyId, client_id: clientId },
    body: { content },
    mediaType: "application/json",
  });
}

/**
 * Deletes an overview note from a client via
 * `DELETE /agency/{agencyId}/clients/{client_id}/notes/{note_id}`.
 *
 * @param agencyId - The owning agency
 * @param clientId - The client the note belongs to
 * @param noteId - The note to delete
 * @returns A cancelable promise resolving to the deletion message payload
 */
/**
 * Deletes a client note via
 * `DELETE /agency/{agencyId}/clients/{client_id}/notes/{note_id}`.
 *
 * @param agencyId - The agency identifier
 * @param clientId - The client identifier
 * @param noteId - The note to delete
 * @returns A cancelable promise resolving to a deletion confirmation message
 */
export function deleteClientNote(
  agencyId: string,
  clientId: string,
  noteId: string,
): CancelablePromise<{ message?: string }> {
  return __request(OpenAPIHousien, {
    method: "DELETE",
    url: "/agency/{agencyId}/clients/{client_id}/notes/{note_id}",
    path: { agencyId, client_id: clientId, note_id: noteId },
  });
}
